import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Loader2, Upload, Check, Palette, Image as ImageIcon, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { apiGet } from "@/lib/api-client";
import { useAuth } from "@/contexts/auth-context";
import { useVenue } from "@/hooks/use-venue";

type BrandData = {
  logo: string;
  name: string;
  colors: { label: string; value: string }[];
};

const emptyBrand: BrandData = {
  logo: "",
  name: "",
  colors: [
    { label: "Primary", value: "#6366f1" },
    { label: "Secondary", value: "#f97316" },
    { label: "Tertiary", value: "#1A1A2E" },
  ],
};

interface VenueEnrichment {
  name: string | null;
  description: string | null;
  primaryColor: string | null;
  logoUrl: string | null;
}

const BrandingPage = () => {
  const { effectiveVenueId } = useAuth();
  const { data: venue } = useVenue(effectiveVenueId);

  const [step, setStep] = useState<"input" | "loading" | "customize">("input");
  const [url, setUrl] = useState("");
  const [brand, setBrand] = useState<BrandData>(emptyBrand);
  const [customLogo, setCustomLogo] = useState<string | null>(null);
  const [pullError, setPullError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handlePull = async () => {
    if (!url.trim()) return;
    setPullError(null);
    setStep("loading");
    try {
      const res = await apiGet<{ success: boolean; data: VenueEnrichment }>(
        `/venues/enrich?url=${encodeURIComponent(url.trim())}`
      );
      if (res.success && res.data) {
        const enriched = res.data;
        setBrand({
          logo: enriched.logoUrl || "",
          name: enriched.name || venue?.name || "",
          colors: [
            { label: "Primary", value: enriched.primaryColor || "#6366f1" },
            { label: "Secondary", value: emptyBrand.colors[1]?.value ?? "#f97316" },
            { label: "Tertiary", value: emptyBrand.colors[2]?.value ?? "#1A1A2E" },
          ],
        });
      } else {
        setBrand({ ...emptyBrand, name: venue?.name || "" });
      }
      setCustomLogo(null);
      setStep("customize");
    } catch {
      setPullError("Could not extract branding from that URL. Please check the address and try again.");
      setStep("input");
    }
  };

  const updateColor = (index: number, value: string) => {
    setBrand((prev) => ({
      ...prev,
      colors: prev.colors.map((c, i) => (i === index ? { ...c, value } : c)),
    }));
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setCustomLogo(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight">Branding</h1>
        <p className="text-muted-foreground mt-1">Pull your venue's branding automatically, then customize it.</p>
      </div>

      <AnimatePresence mode="wait">
        {step === "input" && (
          <motion.div key="input" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Globe className="h-5 w-5 text-primary" /> Enter Your Venue's Website</CardTitle>
                <CardDescription>We'll extract your logo and color scheme automatically.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <Input
                    placeholder="e.g. https://yourvenue.com"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handlePull()}
                    className="flex-1"
                  />
                  <Button onClick={handlePull} disabled={!url.trim()}>
                    <Palette className="h-4 w-4 mr-2" /> Pull My Branding
                  </Button>
                </div>
                {pullError && (
                  <p className="mt-3 text-sm text-destructive">{pullError}</p>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {step === "loading" && (
          <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-muted-foreground font-medium">Analysing <span className="text-foreground">{url}</span>…</p>
            <p className="text-xs text-muted-foreground">Extracting logo and colour palette</p>
          </motion.div>
        )}

        {step === "customize" && (
          <motion.div key="customize" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="space-y-6">
            {/* Success banner */}
            <div className="flex items-center gap-3 rounded-lg border border-accent/30 bg-accent/10 px-4 py-3">
              <Check className="h-5 w-5 text-accent" />
              <p className="text-sm font-medium">Branding pulled from <span className="text-foreground">{url}</span></p>
              <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setStep("input")}>
                <RotateCcw className="h-3.5 w-3.5 mr-1" /> Try another
              </Button>
            </div>

            {/* Logo */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg"><ImageIcon className="h-5 w-5 text-primary" /> Logo</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-6">
                  <div className="flex h-24 w-24 items-center justify-center rounded-xl border-2 border-dashed border-border bg-muted/50 p-2">
                    {(customLogo || brand.logo) ? (
                      <img src={customLogo || brand.logo} alt={brand.name} className="max-h-full max-w-full object-contain" />
                    ) : (
                      <ImageIcon className="h-8 w-8 text-muted-foreground" />
                    )}
                  </div>
                  <div className="space-y-2">
                    {brand.name && (
                      <p className="text-sm text-muted-foreground">Detected: <span className="font-medium text-foreground">{brand.name}</span></p>
                    )}
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                    <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                      <Upload className="h-3.5 w-3.5 mr-1" /> Upload different logo
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Colors */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg"><Palette className="h-5 w-5 text-primary" /> Colour Palette</CardTitle>
                <CardDescription>Click any swatch to adjust it.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  {brand.colors.map((color, i) => (
                    <div key={i} className="space-y-2">
                      <label className="text-xs font-medium text-muted-foreground">{color.label}</label>
                      <div className="relative group">
                        <div
                          className="h-20 w-full rounded-lg border border-border cursor-pointer transition-shadow hover:shadow-md"
                          style={{ backgroundColor: color.value }}
                          onClick={() => document.getElementById(`color-${i}`)?.click()}
                        />
                        <input
                          id={`color-${i}`}
                          type="color"
                          value={color.value}
                          onChange={(e) => updateColor(i, e.target.value)}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                        />
                      </div>
                      <Input
                        value={color.value}
                        onChange={(e) => updateColor(i, e.target.value)}
                        className="text-xs font-mono h-8"
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Preview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Preview</CardTitle>
                <CardDescription>How your branding will look on leaderboard displays.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-xl overflow-hidden border border-border" style={{ backgroundColor: brand.colors[2]?.value || "#1A1A2E" }}>
                  <div className="p-6 flex items-center gap-4 border-b" style={{ borderColor: `${brand.colors[0]?.value}33` }}>
                    {(customLogo || brand.logo) && (
                      <img src={customLogo || brand.logo} alt="Logo" className="h-10 object-contain" />
                    )}
                    <div>
                      <h3 className="font-bold text-white text-lg">{brand.name || "Your Venue"} Leaderboard</h3>
                      <p className="text-white/60 text-xs">Sales Competition</p>
                    </div>
                  </div>
                  <div className="p-4 space-y-2">
                    {["Sarah J.", "Mike T.", "Emma R."].map((name, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-lg px-4 py-3" style={{ backgroundColor: `${brand.colors[0]?.value}15` }}>
                        <span className="text-lg font-bold" style={{ color: i === 0 ? brand.colors[1]?.value : "#ffffff99" }}>#{i + 1}</span>
                        <span className="text-white font-medium flex-1">{name}</span>
                        <span className="font-bold" style={{ color: brand.colors[0]?.value }}>${(320 - i * 85).toFixed(0)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button size="lg">
                <Check className="h-4 w-4 mr-2" /> Save Branding
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BrandingPage;
